import { Control, useController } from "react-hook-form";
import styled from "styled-components";
import { FC, useEffect, useMemo, useState } from "react";
import { UnitItem, UnitsDropdown } from "../ui-kit";
import { MeasurementUnits } from "@fena/toolkit-types";

const Container = styled.div`
  padding-top: 10px;
  width: 100%;
`;

export enum UnitTypes {
  LENGTH = "length",
  WEIGHT = "weight",
  VOLUME = "volume",
}

interface UnitsDropDownFormFieldProps {
  name: string;
  unitName: string;
  className?: string;
  control?: Control<any>;
  label?: string;
  unitType?: UnitTypes;
  manuallyItems?: MeasurementUnits[];
  unitValue?: MeasurementUnits;
  disabled?: boolean;
  reverse?: boolean;
}

export const UnitsDropDownFormField: FC<UnitsDropDownFormFieldProps> = ({
  name,
  unitName,
  className,
  control,
  label,
  manuallyItems,
  unitValue,
  unitType = UnitTypes.LENGTH,
  disabled,
  reverse,
}) => {
  const [localUnitValue, setLocalUnitValue] = useState<MeasurementUnits>(
    unitValue ??
      manuallyItems?.[0] ??
      (unitType === UnitTypes.LENGTH
        ? MeasurementUnits.CENTIMETERS
        : unitType === UnitTypes.VOLUME
        ? MeasurementUnits.MILLILITRES
        : MeasurementUnits.KILOGRAMS)
  );

  const {
    field,
    fieldState: { error },
  } = useController({ name, control });
  const { field: unitField } = useController({ name: unitName, control });

  const handleChange = (val: UnitItem) => {
    field.onChange(val.value);
    unitField.onChange(val.unit);
    setLocalUnitValue(val.unit as MeasurementUnits);
  };

  useEffect(() => {
    unitValue && setLocalUnitValue(unitValue);
  }, [unitValue]);

  const items = useMemo(() => {
    switch (unitType) {
      case UnitTypes.LENGTH:
        return [
          MeasurementUnits.CENTIMETERS,
          MeasurementUnits.MILLIMETRES,
          MeasurementUnits.METRES,
          MeasurementUnits.FEET,
          MeasurementUnits.INCHES,
        ];
      case UnitTypes.VOLUME:
        return [
          MeasurementUnits.MILLILITRES,
          MeasurementUnits.LITRES,
          MeasurementUnits.PINTS,
          MeasurementUnits.GALLONS,
        ];
      case UnitTypes.WEIGHT:
        return [
          MeasurementUnits.KILOGRAMS,
          MeasurementUnits.GRAMS,
          MeasurementUnits.POUNDS,
          MeasurementUnits.TONES,
        ];
    }
  }, [unitType]);

  return (
    <Container className={className}>
      <UnitsDropdown
        reverse={reverse}
        disabled={disabled}
        items={manuallyItems ?? items}
        label={label}
        valueError={error?.message}
        onChange={handleChange}
        value={field.value}
        unitValue={localUnitValue}
      />
    </Container>
  );
};
